import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import {
  InsuranceCoverageRequest,
  InsuranceCoverageResponse,
  InsurancePortfolioSummary,
  LenderInsuranceApi,
  UninsuredAccountRow,
} from '../tokens/lender-insurance.token';

/** {@code GET /api/v1/lender/portfolio} row (B6 LenderPortfolioRowDTO) — fields used here. */
interface PortfolioRowWire {
  loanReference: string;
  borrowerCui: string;
  borrowerName: string;
  currency: string;
  outstandingBalance: number;
  status: string;
  riskGrade: string | null;
  edName: string | null;
}

/** {@code POST /api/v1/lender/insurance/coverage} response wire (B6). */
interface CoverageResponseWire {
  requestReference: string;
  scope: string;
  loanReference: string | null;
  insurerPartner: string;
  status: string;
}

/**
 * REST-only {@link LenderInsuranceApi} (EM-699 / F6 — EM-711).
 *
 * The portfolio summary and the uninsured-accounts list are synthesised from the lender
 * portfolio (Rule 10 preferred pattern — B6 exposes no insurer-stats read). Only active
 * loans ({@code ACTIVE} / {@code RESTRUCTURED}) with an outstanding balance are surfaced as
 * "recommended for coverage". The coverage request hits the dedicated B6 endpoint.
 */
@Injectable()
export class LenderInsuranceRestService implements LenderInsuranceApi {
  private readonly http = inject(HttpClient);

  getPortfolioSummary(): Observable<InsurancePortfolioSummary> {
    return this.portfolio().pipe(
      map(rows => ({
        totalExposure: rows.reduce((a, r) => a + (r.outstandingBalance ?? 0), 0),
        currency: rows[0]?.currency ?? 'EUR',
        loanCount: rows.length,
      })),
    );
  }

  getUninsuredAccounts(): Observable<UninsuredAccountRow[]> {
    return this.portfolio().pipe(
      map(rows =>
        rows
          .filter(r => (r.status === 'ACTIVE' || r.status === 'RESTRUCTURED') && (r.outstandingBalance ?? 0) > 0)
          .map(r => ({
            loanReference: r.loanReference,
            borrowerCui: r.borrowerCui,
            borrowerName: r.borrowerName,
            edName: r.edName,
            outstanding: r.outstandingBalance,
            currency: r.currency,
            riskGrade: r.riskGrade,
          })),
      ),
    );
  }

  requestCoverage(request: InsuranceCoverageRequest): Observable<InsuranceCoverageResponse> {
    const body: Record<string, unknown> = { insurerPartner: request.insurerPartner };
    if (request.loanReference) body['loanReference'] = request.loanReference;
    if (request.coverageType) body['coverageType'] = request.coverageType;
    return this.http.post<CoverageResponseWire>(API_PATHS.LENDER_INSURANCE_COVERAGE, body).pipe(
      map(r => ({
        requestReference: r.requestReference,
        scope: r.scope,
        loanReference: r.loanReference,
        insurerPartner: r.insurerPartner,
        status: r.status,
      })),
    );
  }

  private portfolio(): Observable<PortfolioRowWire[]> {
    const params = new HttpParams().set('size', '500');
    return this.http
      .get<PortfolioRowWire[]>(API_PATHS.LENDER_SERVICING_PORTFOLIO, { params })
      .pipe(map(rows => rows ?? []));
  }
}
